import { useState } from 'react';
import { Link } from 'react-router';
import { instagramAuthorizeUrl } from '../../api/endpoints';
import { ErrorNote } from '../../components/ErrorNote';
import { Field } from '../../components/Field';
import { Spinner } from '../../components/Spinner';
import { InstagramIcon } from '../../lib/brandIcons';

/**
 * "Continue with Instagram" — Business Login for Instagram. A redirect, not a
 * popup: the browser leaves for instagram.com, the CRM callback relays a
 * short-lived grant back to this URL, and ConnectForm redeems it (see
 * oauth/oauthReturn.ts). The inbox name travels in the sign-in state so it is
 * still known when the browser returns.
 *
 * `facebookAvailable` names the other way in: an account linked to a Facebook
 * Page connects from the Messenger picker on the same sign-in as the Page.
 */
export function InstagramLogin({ facebookAvailable }: { facebookAvailable: boolean }) {
  const [name, setName] = useState('');
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    setError(null);
    setLeaving(true);
    try {
      const { url } = await instagramAuthorizeUrl({
        returnTo: window.location.href,
        ...(name.trim() ? { name: name.trim() } : {}),
      });
      window.location.assign(url);
    } catch (e) {
      setLeaving(false);
      setError(e instanceof Error ? e.message : 'Could not start the Instagram sign-in.');
    }
  };

  return (
    <div className="card card-pad connect-form wa-embed">
      {error && <ErrorNote error={error} />}
      <p className="wa-embed-copy">
        Sign in with the Instagram professional account (business or creator) whose messages should land
        here. Instagram asks you to allow message access, then brings you back to finish. No Facebook Page
        is needed.
      </p>
      <Field label="Inbox name (optional)" hint="Defaults to the account's @handle.">
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Instagram"
          disabled={leaving}
        />
      </Field>
      <div className="connect-form-actions wa-embed-actions">
        <button type="button" className="btn btn-primary wa-embed-btn" onClick={() => void start()} disabled={leaving}>
          {leaving ? <Spinner /> : <InstagramIcon size={15} tint="currentColor" />}
          {leaving ? 'Sending you to Instagram…' : 'Continue with Instagram'}
        </button>
      </div>
      {facebookAvailable && (
        <p className="connect-panel-hint">
          Is the account linked to a Facebook Page? Connect it from{' '}
          <Link to="/connect/facebook">Facebook Messenger</Link> instead — one sign-in covers the Page and its
          Instagram account, and the Page's token never expires.
        </p>
      )}
    </div>
  );
}
